/* Drill-down direction — détail d'un devoir : rendus + corrections (spec 10a,
   Écran B). Lecture seule : la direction ne corrige/publie/override jamais. */

import { useEffect, useState } from 'react';
import { Eye } from '@phosphor-icons/react';
import { useNavigate, useParams } from '../lib/routing';
import Header from '../components/header';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { api } from '../lib/api';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';

interface Row {
  classroom_student_id: string;
  first_name: string;
  last_name: string;
  status: string;
  submitted_at: string | null;
  photos: string[];
}
interface Detail {
  id: string;
  title: string;
  format: string;
  due_at: string | null;
  submissions: Row[];
}
interface CorrItem {
  sectionIndex: number;
  sectionTitle: string;
  score: number;
  max: number;
  feedback: string;
  unread?: boolean;
  letter?: string;
  scaleId?: string;
  letterLabel?: string;
}
interface CorrResult {
  totalScore: number;
  totalMax: number;
  overallFeedback: string;
  items: CorrItem[];
  model: string;
}
interface CorrRow {
  result: CorrResult;
  teacher_override: CorrResult | null;
  published_to_student: boolean;
}

const STATUS_LABEL: Record<string, string> = { not_started: 'À faire', draft: 'En cours', submitted: 'Rendu' };
const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'outline'> = { not_started: 'outline', draft: 'secondary', submitted: 'default' };

export default function DirectionAssignmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [corr, setCorr] = useState<CorrRow | null | undefined>(undefined);

  useEffect(() => {
    if (!id) return;
    api.direction.supervision.assignmentSubmissions
      .query({ assignmentId: id })
      .then((d) => setDetail(d as Detail))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [id]);

  const toggleCorrection = async (studentId: string) => {
    if (openId === studentId) {
      setOpenId(null);
      return;
    }
    setOpenId(studentId);
    setCorr(undefined);
    if (!id) return;
    try {
      const c = await api.direction.supervision.assignmentCorrection.query({
        assignmentId: id,
        classroomStudentId: studentId,
      });
      setCorr(c as CorrRow | null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setCorr(null);
    }
  };

  const submitted = detail ? detail.submissions.filter((r) => r.status === 'submitted').length : 0;

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <Header />
      <HeaderContent width="md" showBurger onBack={() => navigate(-1)} backLabel="Retour" eyebrow="Mode direction" eyebrowIcon={<Eye size={11} weight="bold" />}>
        <h1 className={HEADER_TITLE_CLASS}>{detail?.title ?? 'Devoir'}</h1>
      </HeaderContent>
      <div className="max-w-[880px] mx-auto px-4 pt-4 pb-16 flex-1 w-full">
        {error && <Alert className="mb-4">{error}</Alert>}

        {!loading && detail && detail.submissions.length > 0 && (
          <div className="flex items-center gap-3 mb-4 px-4 py-2.5 rounded-card bg-card border border-border">
            <span className="text-[13px] font-semibold text-foreground tabular-nums">
              {submitted}/{detail.submissions.length} rendu{detail.submissions.length > 1 ? 's' : ''}
            </span>
            <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all"
                style={{ width: detail.submissions.length ? `${(submitted / detail.submissions.length) * 100}%` : '0%' }}
              />
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col gap-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-[58px] rounded-card" />)}
          </div>
        ) : !detail || detail.submissions.length === 0 ? (
          <p className="text-[13px] text-muted-foreground py-4">Aucun élève n'a ce devoir pour l'instant.</p>
        ) : (
          <div className="rounded-card border border-border overflow-hidden shadow-card">
            {detail.submissions.map((r, i) => (
              <div key={r.classroom_student_id}>
                {i > 0 && <Separator />}
                <div className="px-4 py-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[14px] text-foreground">{r.first_name} {r.last_name}</div>
                      {r.submitted_at && (
                        <div className="text-[12px] text-muted-foreground mt-0.5">
                          Rendu le {new Date(r.submitted_at).toLocaleDateString('fr-FR')}
                        </div>
                      )}
                    </div>
                    <Badge variant={STATUS_VARIANT[r.status] ?? 'outline'}>{STATUS_LABEL[r.status] ?? r.status}</Badge>
                    {r.status === 'submitted' && (
                      <Button variant="secondary" size="sm" className="min-h-11 min-w-11" onClick={() => void toggleCorrection(r.classroom_student_id)}>
                        {openId === r.classroom_student_id ? 'Fermer' : 'Voir la correction'}
                      </Button>
                    )}
                  </div>

                  {r.photos.length > 0 && (
                    <div className="flex gap-2 mt-3 flex-wrap">
                      {r.photos.map((url, pi) => (
                        <a key={pi} href={url} target="_blank" rel="noreferrer">
                          <img src={url} alt={`Rendu — photo ${pi + 1}`} className="w-[72px] h-[72px] object-cover rounded-lg border border-border" />
                        </a>
                      ))}
                    </div>
                  )}

                  {openId === r.classroom_student_id && (
                    <div className="mt-3 pt-3 border-t border-border flex flex-col gap-3">
                      {corr === undefined ? (
                        <p className="text-[13px] text-muted-foreground">Chargement…</p>
                      ) : corr === null ? (
                        <p className="text-[13px] text-muted-foreground">Pas encore de correction pour cet élève.</p>
                      ) : (
                        <ReadOnlyCorrection row={corr} />
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ReadOnlyCorrection({ row }: { row: CorrRow }) {
  const effective = row.teacher_override ?? row.result;
  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[13px] font-extrabold text-foreground tabular-nums">
          {effective.totalMax > 0 ? `Total : ${effective.totalScore} / ${effective.totalMax}` : 'Notation par lettres'}
        </div>
        <Badge variant={row.published_to_student ? 'default' : 'secondary'}>
          {row.published_to_student ? 'Publiée à l\'élève' : 'Non publiée'}
        </Badge>
      </div>
      {effective.items.map((it, i) => (
        <div key={i} className="border border-border rounded-card p-3 flex flex-col gap-1.5">
          <div className="font-semibold text-[13.5px] text-foreground">{it.sectionTitle || `Section ${it.sectionIndex + 1}`}</div>
          {it.unread ? (
            <Badge variant="secondary">Non lue</Badge>
          ) : (
            <div className="text-[13px] text-muted-foreground">{it.scaleId !== undefined ? `Lettre : ${it.letter ?? '—'}${it.letterLabel ? ` — ${it.letterLabel}` : ''}` : `Note : ${it.score} / ${it.max}`}</div>
          )}
          {it.feedback && <p className="m-0 text-[13px] text-foreground">{it.feedback}</p>}
        </div>
      ))}
      {effective.overallFeedback && (
        <div className="flex flex-col gap-1">
          <span className="text-[12px] font-semibold text-muted-foreground">Appréciation générale</span>
          <p className="m-0 text-[13px] text-foreground">{effective.overallFeedback}</p>
        </div>
      )}
    </>
  );
}
